import {
  Badge,
  CustomProfileField,
  CustomProfileFieldList,
  CustomProfileFieldOptions,
  Group,
  Rank,
  Role
// eslint-disable-next-line import/no-internal-modules
} from '@insided/deprecated-control/UserOverview/Columns'
import {
  FilterValue,
  FilterValueList,
  normalizeUserRoles,
  prepareBadges, prepareGroups,
  prepareRanks
// eslint-disable-next-line import/no-internal-modules
} from '@insided/deprecated-control/UserOverview/Filters'
// eslint-disable-next-line import/no-internal-modules
import { Filter, Filters } from '@insided/deprecated-control/overview'
// eslint-disable-next-line import/no-internal-modules
import { FilterOptionItem } from '@insided/deprecated-control/overview/filters/FilterOption'
import objectKeys from '@insided/shared/utils/objectKeys'
import { ONE_DAY_IN_MILLISECONDS } from '@insided/shared/utils/dateFormatting'

interface Config {
  mainRoles: Role[]
  customRoles: Role[]
  badges: Badge[]
  ranks: Rank[]
  groups: Group[]
  customProfileFields: CustomProfileFieldList
}

const isConditionEmptyNotEmpty = (condition: string) => ['empty', 'not_empty'].includes(condition)
const getObjectEntry = <T = unknown>(obj: {}, index: number = 0): [string, T] => Object.entries(obj)[index] as [string, T]

const numberFilterValueGenerator = (filterValue: FilterValue): { condition: string, data: any} => {
  if (Object.keys(filterValue).length > 1) {
    const [, firstValue] = getObjectEntry<number>(filterValue)
    const [, secondValue] = getObjectEntry<number>(filterValue, 1)

    return {
      condition: 'between',
      data: [firstValue, secondValue]
    }
  }

  const [condition, firstValue] = getObjectEntry<number>(filterValue)

  return {
    condition,
    data: firstValue
  }
}

const textFilterValueGenerator = (filterValue: FilterValue): string => {
  const [_, value] = getObjectEntry<string>(filterValue)

  return value
}

const advancedTextFilterValueGenerator = (filterValue: FilterValue): Filter => {
  const [condition, value] = getObjectEntry<string>(filterValue)
  const isEmptyNotEmpty = isConditionEmptyNotEmpty(condition)

  return {
    condition,
    data: isEmptyNotEmpty ? '' : value,
    showInput: !isEmptyNotEmpty
  }
}

const listFilterValueGenerator = (filterValue: FilterValue, items: FilterOptionItem[]): {
  condition: string
  data: FilterOptionItem[]
  showItems: boolean
} => {
  const [condition, value] = getObjectEntry<number | number[]>(filterValue)
  const isEmptyNotEmpty = isConditionEmptyNotEmpty(condition)

  return {
    condition,
    data: isEmptyNotEmpty ? [] : items.filter(item => (value as number[]).includes(item.id as number)),
    showItems: !isEmptyNotEmpty
  }
}

const filterGenerator: Record<string, (filterValue: FilterValue, config: Config) => Filter> = {
  role: (filterValue, { mainRoles }) => ({
    primaryRole: listFilterValueGenerator(filterValue, normalizeUserRoles(mainRoles))
  }),
  custom_roles: (filterValue, { customRoles }) => ({
    customRoles: listFilterValueGenerator(filterValue, normalizeUserRoles(customRoles))
  }),
  rank: (filterValue, { ranks }) => ({
    rank: listFilterValueGenerator(filterValue, prepareRanks(ranks))
  }),
  badges: (filterValue, { badges }) => ({
    badge: listFilterValueGenerator(filterValue, prepareBadges(badges))
  }),
  groups: (filterValue, { groups }) => ({
    group: listFilterValueGenerator(filterValue, prepareGroups(groups))
  }),
  join_date: (filterValue) => ({
    joinDate: dateFilterValueGenerator(filterValue)
  }),
  last_activity: (filterValue) => ({
    lastActivity: dateFilterValueGenerator(filterValue)
  }),
  last_visit: (filterValue) => ({
    lastLogin: dateFilterValueGenerator(filterValue)
  }),
  replies: (filterValue) => ({
    replies: numberFilterValueGenerator(filterValue)
  }),
  topics: (filterValue) => ({
    topics: numberFilterValueGenerator(filterValue)
  }),
  points: (filterValue) => ({
    points: numberFilterValueGenerator(filterValue)
  }),
  search: (filterValue) => ({
    search: textFilterValueGenerator(filterValue)
  }),
  email: (filterValue) => ({
    email: advancedTextFilterValueGenerator(filterValue)
  })
}

const customProfileFieldListFilterGenerator = (filterValue: FilterValue, customProfileField: CustomProfileField): Filter => {
  const [condition, value] = getObjectEntry<string[]>(filterValue)
  const { id, data } = customProfileField
  const isEmptyNotEmpty = isConditionEmptyNotEmpty(condition)

  const filterValues: FilterOptionItem[] = []
  if (!isEmptyNotEmpty) {
    const options = data as CustomProfileFieldOptions
    objectKeys(options).forEach(key => {
      if (value.includes(key)) {
        filterValues.push({
          id: key,
          value: key,
          name: options[key]
        })
      }
    })
  }

  return {
    [`profileField_${id}`]: {
      condition,
      data: filterValues,
      showItems: !isEmptyNotEmpty
    }
  }
}

const dateFilterValueGenerator = (filterValue: FilterValue): { condition: string, data: string | { from?: string, to?: string }, type: string, unit?: string } => {
  const { from, to } = filterValue as { from?: string, to?: string }
  // between/eq
  if (from && to) {
    const fromDate = new Date(from)
    const toDate = new Date(to)
    const diffInMilliseconds = toDate.getTime() - fromDate.getTime()
    const diffInDays = diffInMilliseconds / ONE_DAY_IN_MILLISECONDS

    if (diffInDays < 1) {
      return {
        condition: 'eq',
        data: from,
        type: 'absolute'
      }
    }

    return {
      condition: 'range',
      data: { from, to },
      type: 'range'
    }
  }

  if (from) {
    return parseRelativePeriod(from, 'from')
  }

  return parseRelativePeriod(to as string, 'to')
}

const parseRelativePeriod = (value: string, operator: string): {condition: string, data: string | { from?: string, to?: string }, type: string, unit?: string } => {
  if (value?.startsWith('P')) {
    return {
      condition: operator === 'from' ? 'lt' : 'gt',
      data: value.replace(/\D+/g, ''),
      type: 'relative',
      unit: 'day'
    }
  }

  return {
    condition: operator,
    data: value,
    type: 'absolute'
  }
}

const customProfileFieldDateFilterGenerator = (filterValue: FilterValue, { id }: CustomProfileField): Filter => ({
  [`profileField_${id}`]: dateFilterValueGenerator(filterValue)
})

const customProfileFieldFilterGenerator: Record<string, (filterValue: FilterValue, customProfileField: CustomProfileField) => Filter> = {
  select: customProfileFieldListFilterGenerator,
  check: customProfileFieldListFilterGenerator,
  radio: customProfileFieldListFilterGenerator,

  birthday: customProfileFieldDateFilterGenerator,
  date: customProfileFieldDateFilterGenerator,

  text: (filterValue, { id }) => ({
    [`profileField_${id}`]: advancedTextFilterValueGenerator(filterValue)
  })
}

export const filterValueListToFilters = (filterValues: FilterValueList, config: Config): Filters => {
  const parsedFilters = {}
  objectKeys(filterValues).forEach(key => {
    if (key in filterGenerator) {
      Object.assign(parsedFilters, filterGenerator[key](filterValues[key], config))

      return
    }

    const profileFieldRegex = /^profile_field_(\d+)$/
    const result = key.match(profileFieldRegex)
    if (!result) {
      return
    }

    const [_, id] = result
    const { customProfileFields } = config
    const customProfileField = customProfileFields.find((item: CustomProfileField) => String(item.id) === String(id))
    if (!customProfileField) {
      return
    }

    const { type } = customProfileField
    if (!(type in customProfileFieldFilterGenerator)) {
      return
    }

    Object.assign(
      parsedFilters,
      customProfileFieldFilterGenerator[type](filterValues[key], customProfileField)
    )
  })

  return parsedFilters
}
